// @ts-nocheck
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material'
import { GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { TFunction } from 'i18next'
import { sum, pluck } from 'ramda'
import numeral from 'numeral'

import { FieldModel } from '@cibo/core'

import { ProgramProgressChip } from '../ProgramProgressChip'
import {
  BenchmarkStatusReasons,
  BenchmarkStatusReasonsProps,
} from '../BenchmarkStatus/BenchmarkStatusReasons'
import { MouseEvent, useState } from 'react'
import { ResponsiveDialog } from '@cibo/ui'

export interface ProgramColumnFunctionProps extends Pick<BenchmarkStatusReasonsProps, 'readOnly'> {
  onClickProgramChip?: (
    event: MouseEvent<HTMLDivElement, MouseEvent>,
    params: GridRenderCellParams<{ dollars: number; credits: number }, FieldModel>
  ) => void
  programId: string
  t: TFunction
}

export const fieldProgramProgressColumn = ({
  programId,
  t,
  ...benchmarkStatusReasonsProps
}: ProgramColumnFunctionProps) => ({
  field: 'status',
  headerName: t('status'),
  flex: 1,
  align: 'center',
  headerAlign: 'center',
  minWidth: 215,
  valueGetter: (params: GridValueGetterParams<string, FieldModel>) => {
    const creditsPotential = params.row.programCreditPerAcrePotential(programId)
    const dollarsPotential = params.row.programDollarPerAcrePotential(programId)

    return {
      credits:
        sum(pluck('credits', creditsPotential).filter(v => !!v) as number[]) * params.row.acres,
      dollars:
        sum(pluck('value', dollarsPotential).filter(v => !!v) as number[]) * params.row.acres,
    }
  },
  renderCell: (params: GridRenderCellParams<{ dollars: number; credits: number }, FieldModel>) => (
    <ProgramProgressCell
      params={params}
      programId={programId}
      t={t}
      {...benchmarkStatusReasonsProps}
    />
  ),
})

const ProgramProgressCell = ({
  params,
  programId,
  t,
  onClickProgramChip,
  ...benchmarkStatusReasonsProps
}: {
  params: GridRenderCellParams<{ dollars: number; credits: number }, FieldModel>
  programId: string
  t: TFunction
} & Pick<BenchmarkStatusReasonsProps, 'readOnly'> &
  Pick<ProgramColumnFunctionProps, 'onClickProgramChip'>) => {
  const { row: field, value } = params
  const [reasonsOpen, setReasonsOpen] = useState(false)

  const progress = field.program(programId)?.progress

  if (progress === 'qualified' || progress === 'prequalified') {
    let amount
    let units: undefined | 'multi' | 'dollars' | 'credits'
    if (value && value.dollars > 0 && value.credits > 0) {
      return (
        <Tooltip
          title={`${t('multiRollup', {
            ...value,
            credits: numeral(value.credits).format('0a'),
            dollars: numeral(value.dollars).format('0a'),
          })}`}
        >
          <Stack alignItems="center" justifyContent="center">
            <ProgramProgressChip
              progress={progress}
              unit="multi"
              size="small"
              onClick={e => !!onClickProgramChip && onClickProgramChip(e, params)}
            />
          </Stack>
        </Tooltip>
      )
    } else if (value && value.dollars > 0) {
      units = 'dollars'
      amount = value.dollars
    } else if (value && value.credits > 0) {
      units = 'credits'
      amount = value.credits
    }
    return (
      <Stack alignItems="center" justifyContent="center">
        <ProgramProgressChip
          progress={progress}
          amount={amount}
          unit={units}
          size="small"
          onClick={e => !!onClickProgramChip && onClickProgramChip(e, params)}
        />
      </Stack>
    )
  }

  if (progress === 'disqualified' || progress === 'needs_info') {
    return (
      <>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
          <ProgramProgressChip
            progress={progress}
            size="small"
            onClick={e => !!onClickProgramChip && onClickProgramChip(e, params)}
          />
          <IconButton
            size="small"
            color="secondary"
            onClick={e => {
              e.stopPropagation()
              setReasonsOpen(true)
            }}
          >
            <InfoOutlinedIcon />
          </IconButton>
        </Stack>
        <ResponsiveDialog
          open={reasonsOpen}
          onClose={() => setReasonsOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{params.row.name}</DialogTitle>
          <DialogContent>
            <BenchmarkStatusReasons
              fieldId={params.row.resourceId}
              reasons={
                params.row
                  .reasons(programId)
                  ?.filter(({ hasResult }) => (progress === 'disqualified' ? hasResult : true)) ||
                []
              }
              {...benchmarkStatusReasonsProps}
            />
          </DialogContent>
          <DialogActions>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              spacing={2}
              flex={1}
            >
              <Box ml={1}>
                <ProgramProgressChip
                  progress={progress}
                  onClick={e => !!onClickProgramChip && onClickProgramChip(e, params)}
                />
              </Box>
              <Button variant="contained" onClick={() => setReasonsOpen(false)}>
                {t('ok')}
              </Button>
            </Stack>
          </DialogActions>
        </ResponsiveDialog>
      </>
    )
  }

  return (
    <Stack alignItems="center" justifyContent="center">
      <ProgramProgressChip
        progress={progress || 'needs_info'}
        size="small"
        onClick={e => !!onClickProgramChip && onClickProgramChip(e, params)}
      />
    </Stack>
  )
}
